/**
 * Lunch Expense API client
 */

import apiClient from '@/core/api/client'
import { API_ENDPOINTS } from '@/core/api/endpoints'
import type {
  LunchExpense,
  CreateLunchExpenseInput,
  UpdateLunchExpenseInput,
  LunchExpenseFilters,
  LunchExpenseListResponse,
  YearlySummary,
} from '../types/lunchExpense.types'

export const lunchExpenseApi = {
  // ==================== Admin Endpoints ====================

  /**
   * Get all expenses (Admin/HR only)
   */
  getExpensesAdmin: async (filters?: LunchExpenseFilters): Promise<LunchExpenseListResponse> => {
    const response = await apiClient.get<LunchExpenseListResponse>(
      API_ENDPOINTS.LUNCH_EXPENSE.ADMIN_EXPENSES,
      { params: filters }
    )
    return response.data
  },

  /**
   * Create a new expense (Admin/HR only)
   */
  createExpense: async (data: CreateLunchExpenseInput): Promise<{ data: LunchExpense; message: string }> => {
    const response = await apiClient.post<{ data: LunchExpense; message: string }>(
      API_ENDPOINTS.LUNCH_EXPENSE.ADMIN_EXPENSES,
      data
    )
    return response.data
  },

  /**
   * Get a single expense (Admin/HR only)
   */
  getExpenseAdmin: async (id: string): Promise<{ data: LunchExpense }> => {
    const response = await apiClient.get<{ data: LunchExpense }>(
      API_ENDPOINTS.LUNCH_EXPENSE.ADMIN_EXPENSE(id)
    )
    return response.data
  },

  /**
   * Update an expense (Admin/HR only)
   */
  updateExpense: async (
    id: string,
    data: UpdateLunchExpenseInput
  ): Promise<{ data: LunchExpense; message: string }> => {
    const response = await apiClient.put<{ data: LunchExpense; message: string }>(
      API_ENDPOINTS.LUNCH_EXPENSE.ADMIN_EXPENSE(id),
      data
    )
    return response.data
  },

  /**
   * Delete an expense (Admin/HR only)
   */
  deleteExpense: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LUNCH_EXPENSE.ADMIN_EXPENSE(id)
    )
    return response.data
  },

  // ==================== Employee Endpoints (Read-only) ====================

  /**
   * Get all expenses (all authenticated users)
   */
  getExpenses: async (filters?: LunchExpenseFilters): Promise<LunchExpenseListResponse> => {
    const response = await apiClient.get<LunchExpenseListResponse>(
      API_ENDPOINTS.LUNCH_EXPENSE.EXPENSES,
      { params: filters }
    )
    return response.data
  },

  /**
   * Get expense summary by year (all authenticated users)
   */
  getSummary: async (year?: number): Promise<{ data: YearlySummary }> => {
    const response = await apiClient.get<{ data: YearlySummary }>(
      API_ENDPOINTS.LUNCH_EXPENSE.SUMMARY,
      { params: { year } }
    )
    return response.data
  },

  /**
   * Get a single expense (all authenticated users)
   */
  getExpense: async (id: string): Promise<{ data: LunchExpense }> => {
    const response = await apiClient.get<{ data: LunchExpense }>(
      API_ENDPOINTS.LUNCH_EXPENSE.EXPENSE(id)
    )
    return response.data
  },
}
